import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, Divider, InputNumber, Row, Select, Space, Switch, Tag } from "antd";
import { v4 as uuidv4 } from "uuid";
import { fetchProducts } from "../utils/fetch";
import { calculatePartitionPrice } from "../utils/partitionPrice";
import { currencyFormat } from "../utils/price";
import {
  GLASS_CATALOG,
  INSTALLMENT_OPTIONS,
  OPENING_CONFIGS,
  SECTION_WIDTH_MIN,
  SECTION_WIDTH_MAX,
  DEFAULT_MAX_HEIGHT,
  DESIGNER_DEFAULT_PERCENT,
  VAT_DEFAULT_PERCENT,
  resolveGlass,
  normalizeGlassColor,
  getGlassDimensions,
} from "../utils/partitionCatalog";
import Loading from "../../../components/Loading/Loading";

const PARTITION_TYPE_NAME = "Алюминиевые перегородки";

const GLASS_GROUP_ORDER = ["Прозрачное", "Матовое", "Зеркало", "Рифленое", "Дизайнерское"];

const GROUPED_GLASS_OPTIONS = GLASS_GROUP_ORDER.map((group) => ({
  label: group,
  options: GLASS_CATALOG.filter((g) => g.group === group).map((g) => ({
    value: g.name,
    label: g.name,
  })),
}));

const DEFAULT_GLASS = "Прозрачное";

const makeEmptyPartition = (seedProduct) => ({
  uuid: uuidv4(),
  productId: seedProduct._id,
  amount: 1,
  product: {
    productId: seedProduct._id,
    name: seedProduct.name,
    price: seedProduct.price || 0,
    costPrice: seedProduct.costPrice || 0,
    productType: seedProduct.productType?._id || seedProduct.productType,
  },
  partitionConfig: {
    openingHeight: 2000,
    openingWidth: 1400,
    sectionsCount: 2,
    openingConfig: "2 секции в проем",
    glassThickness: 4,
    glassColor: DEFAULT_GLASS,
    glassTempered: false,
    verticalDividers: 0,
    horizontalDividers: 0,
    designerModifierEnabled: false,
    designerModifierPercent: DESIGNER_DEFAULT_PERCENT,
    installmentModifierEnabled: false,
    installmentModifierType: "NONE",
    installmentModifierPercent: 0,
    vatModifierEnabled: false,
    vatModifierPercent: VAT_DEFAULT_PERCENT,
  },
});

const PartitionsInformation = ({
  setToken,
  selectedProducts,
  setSelectedProducts,
  clientType,
  onChange,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const lastClientTypeRef = useRef(clientType);

  useEffect(() => {
    fetchProducts(
      setProducts,
      setIsLoading,
      setToken,
      () => {},
      () => {},
      () => {},
      () => {}
    );
  }, [setToken]);

  const seedProduct = useMemo(
    () => products.find((p) => p?.productType?.name === PARTITION_TYPE_NAME),
    [products]
  );

  const updatePartition = (index, updates) => {
    const next = selectedProducts.map((row, i) => {
      if (i !== index) return row;
      return {
        ...row,
        partitionConfig: { ...row.partitionConfig, ...updates },
      };
    });
    setSelectedProducts(next);
    if (onChange) onChange();
  };

  const addPartition = () => {
    if (!seedProduct) return;
    setSelectedProducts([...selectedProducts, makeEmptyPartition(seedProduct)]);
    if (onChange) onChange();
  };

  const removePartition = (index) => {
    setSelectedProducts(selectedProducts.filter((_, i) => i !== index));
    if (onChange) onChange();
  };

  useEffect(() => {
    if (lastClientTypeRef.current === clientType) return;
    lastClientTypeRef.current = clientType;

    const enableVat = clientType === "ENTITY";
    if (!selectedProducts.length) return;

    const next = selectedProducts.map((row) => {
      if (!row.partitionConfig) return row;
      return {
        ...row,
        partitionConfig: {
          ...row.partitionConfig,
          vatModifierEnabled: enableVat,
          vatModifierPercent: enableVat
            ? row.partitionConfig.vatModifierPercent || VAT_DEFAULT_PERCENT
            : row.partitionConfig.vatModifierPercent,
        },
      };
    });
    setSelectedProducts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientType]);

  if (isLoading) return <Loading />;

  if (!seedProduct) {
    return (
      <div>
        <h1 style={{ marginBottom: 20 }}>Алюминиевые перегородки</h1>
        <Tag color="red">
          Не найден продукт типа "{PARTITION_TYPE_NAME}". Запустите сид:
          <code style={{ marginLeft: 8 }}>node scripts/seed-aluminum-partition.js</code>
        </Tag>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Алюминиевые перегородки</h1>

      {selectedProducts.map((row, index) => (
        <PartitionCard
          key={row.uuid || index}
          index={index}
          config={row.partitionConfig || {}}
          onUpdate={(updates) => updatePartition(index, updates)}
          onRemove={() => removePartition(index)}
        />
      ))}

      <Button type="dashed" onClick={addPartition} block style={{ marginTop: 8 }}>
        + Добавить перегородку
      </Button>
    </div>
  );
};

const PartitionCard = ({ index, config, onUpdate, onRemove }) => {
  const glassInfo = resolveGlass(config.glassColor);
  const storedGlassValue = normalizeGlassColor(config.glassColor) || DEFAULT_GLASS;

  const sectionWidth =
    config.openingWidth && config.sectionsCount
      ? Math.round(config.openingWidth / config.sectionsCount)
      : null;

  const maxHeightWithoutDivider =
    glassInfo?.maxHeightWithoutDivider || DEFAULT_MAX_HEIGHT;

  const warnings = [];
  if (sectionWidth != null && (sectionWidth < SECTION_WIDTH_MIN || sectionWidth > SECTION_WIDTH_MAX)) {
    warnings.push(
      `Ширина секции ${sectionWidth} мм — допустимо от ${SECTION_WIDTH_MIN} до ${SECTION_WIDTH_MAX} мм`
    );
  }
  if (
    config.openingHeight > maxHeightWithoutDivider &&
    (config.horizontalDividers || 0) === 0
  ) {
    warnings.push(
      `Высота ${config.openingHeight} мм превышает максимум ${maxHeightWithoutDivider} мм для "${glassInfo?.name || "этого стекла"}" — обязательно добавьте горизонтальный разделитель`
    );
  }
  if (config.glassTempered && glassInfo && !glassInfo.canTemper) {
    warnings.push(
      `Стекло "${glassInfo.name}" не закаливается — флаг закалки игнорируется`
    );
  }

  const glassDims = getGlassDimensions(
    config.openingHeight,
    config.openingWidth,
    config.sectionsCount
  );

  const filteredOpeningConfigs = OPENING_CONFIGS.filter(
    (c) => !config.sectionsCount || c.sections === config.sectionsCount
  );

  const livePrice = calculatePartitionPrice(config);

  const handleDesignerToggle = (v) => {
    const updates = { designerModifierEnabled: v };
    if (v && (!config.designerModifierPercent || config.designerModifierPercent === 0)) {
      updates.designerModifierPercent = DESIGNER_DEFAULT_PERCENT;
    }
    onUpdate(updates);
  };

  const handleInstallmentType = (value) => {
    const opt = INSTALLMENT_OPTIONS.find((o) => o.value === value);
    if (!opt) return;
    onUpdate({
      installmentModifierType: value,
      installmentModifierEnabled: opt.percent > 0,
      installmentModifierPercent: opt.percent,
    });
  };

  const handleVatToggle = (v) => {
    const updates = { vatModifierEnabled: v };
    if (v && (!config.vatModifierPercent || config.vatModifierPercent === 0)) {
      updates.vatModifierPercent = VAT_DEFAULT_PERCENT;
    }
    onUpdate(updates);
  };

  return (
    <Card
      size="small"
      title={`Перегородка №${index + 1}`}
      extra={
        <Button danger size="small" onClick={onRemove}>
          Удалить
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      <Row gutter={24}>
        <Col span={16}>
          <SectionTitle>Параметры проёма, в мм</SectionTitle>
          <Space size="large" wrap>
            <LabeledNumber
              label="Высота проёма"
              value={config.openingHeight}
              onChange={(v) => onUpdate({ openingHeight: v })}
              min={0}
              suffix="мм"
            />
            <LabeledNumber
              label="Ширина проёма"
              value={config.openingWidth}
              onChange={(v) => onUpdate({ openingWidth: v })}
              min={0}
              suffix="мм"
            />
          </Space>

          <Divider style={{ margin: "16px 0" }} />

          <SectionTitle>Количество секций / Способ открывания</SectionTitle>
          <Space size="large" wrap>
            <LabeledSelect
              label="Количество секций"
              value={config.sectionsCount}
              onChange={(v) => onUpdate({ sectionsCount: v })}
              options={[1, 2, 3, 4, 5, 6].map((n) => ({ value: n, label: String(n) }))}
            />
            <Labeled label="Ширина 1 секции">
              <span style={{ fontWeight: 600 }}>
                {sectionWidth ? `${sectionWidth} мм` : "—"}
              </span>
            </Labeled>
            <LabeledSelect
              label="Перехлёсты / стыки"
              value={config.openingConfig}
              onChange={(v) => onUpdate({ openingConfig: v })}
              options={filteredOpeningConfigs.map((c) => ({
                value: c.label,
                label: c.label,
              }))}
              style={{ minWidth: 280 }}
            />
          </Space>

          <Divider style={{ margin: "16px 0" }} />

          <SectionTitle>Стекло / Разделители</SectionTitle>
          <Space size="large" wrap>
            <Labeled label="Толщина стекла, мм">
              <span style={{ fontWeight: 600 }}>{config.glassThickness ?? 4}</span>
            </Labeled>
            <Labeled label="Цвет стекла">
              <Select
                value={storedGlassValue}
                onChange={(v) => onUpdate({ glassColor: v })}
                options={GROUPED_GLASS_OPTIONS}
                showSearch
                style={{ minWidth: 280 }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Labeled>
            <Labeled label="Закалка">
              <Switch
                checked={!!config.glassTempered}
                onChange={(v) => onUpdate({ glassTempered: v })}
                disabled={glassInfo && !glassInfo.canTemper}
                checkedChildren="Да"
                unCheckedChildren="Нет"
              />
            </Labeled>
            <LabeledNumber
              label="Вертикальные разделители"
              value={config.verticalDividers}
              onChange={(v) => onUpdate({ verticalDividers: v || 0 })}
              min={0}
            />
            <LabeledNumber
              label="Горизонтальные разделители"
              value={config.horizontalDividers}
              onChange={(v) => onUpdate({ horizontalDividers: v || 0 })}
              min={0}
            />
          </Space>

          {glassDims.glassHeight > 0 && (
            <div style={{ marginTop: 12, fontSize: 12, color: "#8c8c8c" }}>
              Размеры стекла: {glassDims.glassHeight} × {Math.round(glassDims.glassWidth)} мм,{" "}
              {glassDims.glassCount} шт.
            </div>
          )}

          {warnings.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {warnings.map((w, i) => (
                <Alert
                  key={i}
                  type="warning"
                  message={w}
                  style={{ marginBottom: 8 }}
                  showIcon
                />
              ))}
            </div>
          )}
        </Col>

        <Col span={8}>
          <SectionTitle>Модификаторы</SectionTitle>

          <Row align="middle" gutter={8} style={{ marginBottom: 8 }}>
            <Col span={10}>Дизайнерские</Col>
            <Col span={6}>
              <Switch
                checked={!!config.designerModifierEnabled}
                onChange={handleDesignerToggle}
                checkedChildren="Да"
                unCheckedChildren="Нет"
              />
            </Col>
            <Col span={8}>
              <InputNumber
                value={config.designerModifierPercent}
                onChange={(v) => onUpdate({ designerModifierPercent: v || 0 })}
                min={0}
                max={100}
                addonAfter="%"
                style={{ width: "100%" }}
                disabled={!config.designerModifierEnabled}
              />
            </Col>
          </Row>

          <Row align="middle" gutter={8} style={{ marginBottom: 8 }}>
            <Col span={10}>Рассрочка</Col>
            <Col span={14}>
              <Select
                value={config.installmentModifierType || "NONE"}
                onChange={handleInstallmentType}
                options={INSTALLMENT_OPTIONS.map((o) => ({
                  value: o.value,
                  label: `${o.label} (${o.percent}%)`,
                }))}
                style={{ width: "100%" }}
              />
            </Col>
          </Row>

          <Row align="middle" gutter={8} style={{ marginBottom: 8 }}>
            <Col span={10}>НДС</Col>
            <Col span={6}>
              <Switch
                checked={!!config.vatModifierEnabled}
                onChange={handleVatToggle}
                checkedChildren="Да"
                unCheckedChildren="Нет"
              />
            </Col>
            <Col span={8}>
              <InputNumber
                value={config.vatModifierPercent}
                onChange={(v) => onUpdate({ vatModifierPercent: v || 0 })}
                min={0}
                max={100}
                addonAfter="%"
                style={{ width: "100%" }}
                disabled={!config.vatModifierEnabled}
              />
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0" }} />

          <SectionTitle>Стоимость</SectionTitle>
          <Tag color="blue" style={{ fontSize: 16, padding: "6px 14px", fontWeight: 600 }}>
            {currencyFormat(livePrice)} тг
          </Tag>
        </Col>
      </Row>
    </Card>
  );
};

const SectionTitle = ({ children }) => (
  <div style={{ fontWeight: 600, marginBottom: 12, color: "#595959" }}>{children}</div>
);

const Labeled = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    <span style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>{label}</span>
    {children}
  </div>
);

const LabeledNumber = ({ label, value, onChange, min, max, suffix }) => (
  <Labeled label={label}>
    <InputNumber
      value={value}
      onChange={onChange}
      min={min}
      max={max}
      addonAfter={suffix}
      style={{ width: suffix ? 140 : 100 }}
    />
  </Labeled>
);

const LabeledSelect = ({ label, value, onChange, options, style }) => (
  <Labeled label={label}>
    <Select value={value} onChange={onChange} options={options} style={{ width: 140, ...style }} />
  </Labeled>
);

export default PartitionsInformation;
